import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useAppDispatch, useAppSelector } from '@verantos-dev/app-state-base';
import { emitSyncConceptGroupSelections } from '@verantos-dev/app-state-research-assistant';
import { useCohortBuilderData } from '@verantos-dev/data-access-cohorts';
import {
  CohortBuilderDefinitionLegacy,
  CohortDefinitionStatus,
  FilterTypeLegacy,
  SimpleEventType,
  SimpleEventTypeLegacy,
} from '@verantos-dev/types-cohorts';

import { useCanAddFilters } from './useCanAddFilters';
import { useCanSaveCohortItem } from './useCanSaveCohortItem';
import { useCohortBuilderItemIsInUse } from './useCohortBuilderItemIsInUse';
import { useCohortEditable } from './useCohortEditable';
import { useConceptFilters } from './useConceptFilters';
import { useNumericFilters } from './useNumericFilters';
import { useRelatedEntityFilters } from './useRelatedEntityFilters';
import { useSimpleEventScroll } from './useSimpleEventScroll';
import { useUniquenessFilters } from './useUniquenessFilters';
import {
  clearConceptGroupsConfidence,
  clearModal,
  markConceptGroupSelectionsAsReported,
  setActiveCohortBuilderItemFilterInputFieldValue,
  setActiveCohortBuilderItemName,
  setActiveCohortBuilderItemSimpleEventType,
  setSimpleEvent,
} from '../cohortBuilderSlice';
import { selectConceptGroupSelections } from '../concept-groups/selectors';
import {
  NAME_MAX_CHARACTER_LENGTH,
  NAME_VALID_CHARACTERS_WITHOUT_DOUBLE_QUOTE,
} from '../constants/constants';
import {
  selectActiveCohortBuilderItemId,
  selectActiveCohortBuilderItemInputFieldConfigMap,
  selectActiveCohortBuilderItemName,
  selectActiveCohortBuilderItemSimpleEventType,
} from '../selectors/cohortBuilderEditModalSelectors';
import { selectModal } from '../selectors/cohortBuilderWorkflowSelectors';
import {
  selectCohortDefinition,
  selectDerivedEvents,
  selectSimpleEvents,
  selectSubjectVariables,
} from '../selectors/cohortDefinitionSelectors';
import { selectConceptGroups } from '../selectors/conceptGroupSelectors';
import { validateName } from '../utils/checkNameUniqueness';
import { getFullCohortDslForSimpleEvent } from '../utils/dsl-generators/getFullCohortDslForSimpleEvent';
import { extractNewFiltersFromFilters } from '../utils/extractNewFiltersFromFilters';

/** Map SimpleEventType/SimpleEventTypeLegacy to OMOP table string for backend */
const simpleEventTypeToContainerType: Record<string, string> = {
  [SimpleEventTypeLegacy.CONDITION_OCCURRENCE]: 'CONDITION_OCCURRENCE',
  [SimpleEventTypeLegacy.DRUG_EXPOSURE]: 'DRUG_EXPOSURE',
  [SimpleEventTypeLegacy.MEASUREMENT]: 'MEASUREMENT',
  [SimpleEventTypeLegacy.OBSERVATION]: 'OBSERVATION',
  [SimpleEventTypeLegacy.PROCEDURE_OCCURRENCE]: 'PROCEDURE_OCCURRENCE',
  [SimpleEventTypeLegacy.VISIT_OCCURRENCE]: 'VISIT_OCCURRENCE',
  [SimpleEventType.CONDITION_OCCURRENCE]: 'CONDITION_OCCURRENCE',
  [SimpleEventType.DRUG_EXPOSURE]: 'DRUG_EXPOSURE',
  [SimpleEventType.MEASUREMENT]: 'MEASUREMENT',
  [SimpleEventType.OBSERVATION]: 'OBSERVATION',
  [SimpleEventType.PROCEDURE_OCCURRENCE]: 'PROCEDURE_OCCURRENCE',
  [SimpleEventType.VISIT_OCCURRENCE]: 'VISIT_OCCURRENCE',
};

export const useSimpleEventEdit = () => {
  const [selectedComboBoxValue, setSelectedComboxBoxValue] = useState<string | undefined>(
    undefined
  );
  const dispatch = useAppDispatch();
  const { data: cohortData, cohortId } = useCohortBuilderData();
  const conceptGroups = useAppSelector(selectConceptGroups);
  const conceptGroupSelections = useAppSelector(selectConceptGroupSelections);
  const isProcessing =
    !!cohortData?.data.latestStatus &&
    [CohortDefinitionStatus.PROCESSING, CohortDefinitionStatus.FINAL].includes(
      cohortData?.data.latestStatus
    );
  const { isEditable } = useCohortEditable();
  const viewMode = !isEditable || isProcessing;

  /** Cohort Builder Workflow - Edit Modal State */
  const simpleEventId = useAppSelector(selectActiveCohortBuilderItemId) ?? uuidv4(); // if no id, then we are creating a new simple event

  // Summary State
  const name = useAppSelector(selectActiveCohortBuilderItemName) ?? '';
  const type = useAppSelector(selectActiveCohortBuilderItemSimpleEventType);

  const modal = useAppSelector(selectModal);
  const isSimpleEventIsInUse = useCohortBuilderItemIsInUse(simpleEventId);

  // Filter State
  const { conceptFilters, onAddConceptFilterClick, onDeleteConceptFilterClick } =
    useConceptFilters();
  const { numericFilters, onAddNumericFilterClick, onDeleteNumericFilterClick } =
    useNumericFilters();
  const { relatedEntityFilters, onAddRelatedEntityFilterClick, onDeleteRelatedEntityFilterClick } =
    useRelatedEntityFilters();
  const { uniquenessFilters, onAddUniquenessFilterClick, onDeleteUniquenessFilterClick } =
    useUniquenessFilters();

  const inputFieldConfigMap =
    useAppSelector(selectActiveCohortBuilderItemInputFieldConfigMap) ?? {};

  const { isSaveAllowed } = useCanSaveCohortItem<SimpleEventTypeLegacy | SimpleEventType>(
    name,
    type,
    [...conceptFilters, ...numericFilters, ...uniquenessFilters, ...relatedEntityFilters]
  );

  const {
    headerRef,
    dialogContentRef,
    conceptFilterRef,
    numericFilterRef,
    uniquenessFilterRef,
    relatedEntityFilterRef,
  } = useSimpleEventScroll(conceptFilters, numericFilters, relatedEntityFilters, uniquenessFilters);

  const { enableAddFilterButtons, addFilterButtonTitleText, typeInputFieldTitleText } =
    useCanAddFilters<SimpleEventTypeLegacy | SimpleEventType>(name, type, isSimpleEventIsInUse);

  const currentCohortDefinition = useAppSelector(selectCohortDefinition);

  /** user to check for duplicate names */
  const subjectVariables = useAppSelector(selectSubjectVariables);
  const derivedEvents = useAppSelector(selectDerivedEvents);
  const simpleEvents = useAppSelector(selectSimpleEvents);

  const [nameInError, setNameInError] = useState<boolean>(false);
  const [nameErrorMsg, setNameErrorMsg] = useState<string>('');

  const onNameBlur = (_id: string, _value: string, _inError: boolean, _trimValue: string) => {
    const { errorMsg, hasError } = validateName(
      simpleEventId,
      name,
      subjectVariables ?? [],
      derivedEvents ?? [],
      simpleEvents ?? []
    );
    setNameErrorMsg(errorMsg);
    setNameInError(hasError);
  };

  // check on modal/card open (on mount) using useEffect:
  useEffect(() => {
    const { errorMsg, hasError } = validateName(
      simpleEventId,
      name,
      subjectVariables ?? [],
      derivedEvents ?? [],
      simpleEvents ?? []
    );
    setNameErrorMsg(errorMsg);
    setNameInError(hasError);
  }, [name, derivedEvents, simpleEvents, subjectVariables, simpleEventId]);

  const onNameChange = (_id: string, value: string) => {
    !viewMode &&
      dispatch(
        setActiveCohortBuilderItemName({
          id: simpleEventId,
          name: value,
        })
      );

    setNameErrorMsg('');
    setNameInError(false);
  };

  const onTypeChange = (_id: string, value: string) => {
    !isSimpleEventIsInUse &&
      !viewMode &&
      value &&
      dispatch(
        setActiveCohortBuilderItemSimpleEventType({
          id: simpleEventId,
          type: value as SimpleEventTypeLegacy | SimpleEventType,
        })
      );
  };

  const onFilterInputValueChange = (
    filterId: string,
    inputFieldId: string,
    value: string,
    filterType: FilterTypeLegacy
  ) => {
    !viewMode &&
      dispatch(
        setActiveCohortBuilderItemFilterInputFieldValue({
          filterType,
          filterId,
          inputFieldId,
          value,
        })
      );
  };

  /** Cohort Builder Edit Modal Action Handlers */
  const onSaveSimpleEvent = (
    emitResearchAssistantUpdate: (cohortDefinition: CohortBuilderDefinitionLegacy) => void
  ) => {
    // Validate name length before saving
    const trimmedName = name.trim();
    if (trimmedName.length > NAME_MAX_CHARACTER_LENGTH) {
      setNameErrorMsg(
        `Name must be ${NAME_MAX_CHARACTER_LENGTH} characters or less. Current length: ${trimmedName.length}`
      );
      setNameInError(true);
      return; // Prevent saving and emitting to Research Assistant
    }

    const _nextSimpleEvent = {
      id: simpleEventId,
      name: trimmedName,
      type: type,
      conceptFilters,
      numericFilters,
      relatedEntityFilters,
      uniquenessFilters,
    };

    if (!viewMode) {
      dispatch(setSimpleEvent(_nextSimpleEvent));
      dispatch(clearConceptGroupsConfidence({ id: simpleEventId }));

      // Sync concept group selections
      if (cohortId && name.trim()) {
        // Optimistically mark selections for this container as reported
        dispatch(
          markConceptGroupSelectionsAsReported({
            containerName: name.trim(),
          })
        );

        // Extract newFilters from concept and related entity filters
        const containerType = type ? simpleEventTypeToContainerType[type] : undefined;
        const newFilters = containerType
          ? [
              ...extractNewFiltersFromFilters(
                conceptFilters,
                'conceptFilter',
                trimmedName,
                containerType,
                conceptGroups,
                0,
                conceptGroupSelections
              ),
              ...extractNewFiltersFromFilters(
                relatedEntityFilters,
                'relatedEntityFilter',
                trimmedName,
                containerType,
                conceptGroups,
                1,
                conceptGroupSelections
              ),
            ]
          : [];

        // Emit sync event to backend
        emitSyncConceptGroupSelections({
          cohortId,
          containerName: name.trim(),
          ...(newFilters.length > 0 ? { newFilters } : {}),
        });
      }

      const { cohortDefinition } = getFullCohortDslForSimpleEvent(
        currentCohortDefinition,
        _nextSimpleEvent
      );

      emitResearchAssistantUpdate(cohortDefinition);
    }
  };

  const onCancelSimpleEvent = () => {
    dispatch(clearModal());
  };

  return {
    /** Cohort Builder Edit Modal State */
    name,
    type,
    modal,
    simpleEventId,
    enableAddFilterButtons,
    conceptFilters,
    numericFilters,
    relatedEntityFilters,
    uniquenessFilters,
    isSaveAllowed,
    inputFieldConfigMap,
    isSimpleEventIsInUse,

    addFilterButtonTitleText,
    typeInputFieldTitleText,

    /** Cohort Builder Edit Modal Internal State Side Effects */
    onNameChange,
    onNameBlur,
    nameInError,
    nameErrorMsg,
    nameValidCharacters: NAME_VALID_CHARACTERS_WITHOUT_DOUBLE_QUOTE,
    onTypeChange,
    onFilterInputValueChange,
    onAddConceptFilterClick,
    onDeleteConceptFilterClick,
    onAddNumericFilterClick,
    onDeleteNumericFilterClick,
    onAddRelatedEntityFilterClick,
    onDeleteRelatedEntityFilterClick,
    onAddUniquenessFilterClick,
    onDeleteUniquenessFilterClick,

    /** Cohort Builder Edit Modal Action Handlers */
    onSaveSimpleEvent,
    onCancelSimpleEvent,

    /** Auto Scroll when adding filters **/
    headerRef,
    dialogContentRef,
    conceptFilterRef,
    numericFilterRef,
    relatedEntityFilterRef,
    uniquenessFilterRef,
    isEditable,
    viewMode,

    selectedComboBoxValue,
    setSelectedComboxBoxValue,
  };
};
